import type { InputRouter, LayerHandle } from "../../ui/input/router.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";
import { PAGE_RULES } from "../rules.ts";
import { flagPanelAction, flagPanelKeyAction, flagToggleView, flagLockReason, toggleDelays, type ToggleDelays, type ToggleInput } from "./flag-toggle.ts";
import type { HudSnapshot } from "./store.ts";

/**
 * The flag panel's keys (ADR-076's amendment of 2026-10-01 and its note of 2026-10-03), named here by their default:
 * the key is the one the page's key table binds the PvP flag to (P1.9-T50, `controls/`), read on each press and as
 * the panel's layer opens, which is never while Settings is open. Every press goes through `controls/press.ts`, so
 * a panel bound to Tab answers it only while the game has focus, and its layer then names no key of its own: the
 * key listener answers the open panel's key too. U opens the flag toggle's panel
 * on the player card, only while the game owns the keyboard, no other panel's layer is open and the character is alive
 * (the Inventory window's layer, which names no key, does not count: U opens the panel over it, ADR-061's note of
 * 2026-10-04); while
 * it is open the panel is a layer of the input router, so U again starts the switch the button offers and closes the
 * panel, or, while a switch counts or the button is locked, only closes it, so U never cancels; Escape closes it. Enter
 * presses the button as it reads (the switch it offers, Cancel while one counts, nothing while it is locked) and closes
 * the panel, but only in a panel U opened: one the card's mark opened leaves Enter to the game, so an Enter meant for
 * the chat never starts or cancels a switch. The router answers a layer's keys only while no text field has focus, so U
 * and Enter type in chat as before. The panel's open state, and whether a key opened it, are the HUD store's, which the
 * card's mark also sets, so a click and a key always agree, the layer follows them either way, and the panel's hint row
 * names Enter only where it is live.
 */

/**
 * What the keys need from the HUD store: its snapshot and a way to hear it change, the panel's open state, and the
 * request the button sends.
 */
export interface FlagPanelStore {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => HudSnapshot;
  setFlagPanel(open: boolean, byKey?: boolean): void;
  requestFlag(wanted: number): boolean;
}

/**
 * The switch's delays from the page's rules.
 */
const DELAYS: ToggleDelays = toggleDelays(PAGE_RULES.constants);

/**
 * Sends what a key asks of the panel at a moment: the preference the action reads from the store's snapshot, or
 * nothing when it reads none. When the button is locked the key says why instead of doing nothing: the panel's
 * status line goes to `notice`.
 *
 * @param store The HUD store.
 * @param nowMs The page clock.
 * @param action `flagPanelAction` for Enter, which presses the button as it reads, or `flagPanelKeyAction` for U.
 * @param notice Shows a line on the message line.
 */
function press(store: FlagPanelStore, nowMs: number, action: (input: ToggleInput, delays: ToggleDelays) => number | null, notice: (line: string) => void): void {
  const { flag, preference, combatantEndsAt, vitals, pvpSwitch, level, inParty } = store.getSnapshot();
  const input: ToggleInput = { nowMs, flag, preference, combatantEndsAt, inCombat: vitals?.inCombat ?? false, dead: vitals?.dead ?? false, pvpSwitch, lockedReason: flagLockReason({ level, inParty, preference }) };
  const wanted = action(input, DELAYS);
  if (wanted !== null) {
    store.requestFlag(wanted);
    return;
  }
  const view = flagToggleView(input, DELAYS);
  if (view.locked) {
    notice(view.line);
  }
}

/**
 * Binds the panel's keys to the page's input router and the HUD store.
 *
 * @param store The HUD store.
 * @param input The page's input router.
 * @param now The page clock (`performance.now`).
 * @param under How many open layers U may open the panel over: the layers of surfaces that name no key and so take
 *   nothing from the game (the Inventory window's, 1 while it is open); none unless given.
 * @param keys The key table, whose PvP flag key is the panel's; the page's unless given.
 * @param notice Shows a line on the message line when a key meets a locked button; none unless given.
 * @returns A function that unbinds them and closes the panel's layer.
 * @example
 * bindFlagPanelKeys(hud, input, () => performance.now(), () => (bag.getSnapshot().open ? 1 : 0));
 */
export function bindFlagPanelKeys(store: FlagPanelStore, input: Pick<InputRouter, "open" | "onKey" | "depth">, now: () => number, under: () => number = () => 0, keys: Pick<KeyTable, "actionOf" | "code"> = pageKeys, notice: (line: string) => void = () => undefined): () => void {
  let layer: LayerHandle | null = null;
  const panelKey = (): void => {
    press(store, now(), flagPanelKeyAction, notice);
    store.setFlagPanel(false);
  };
  const enter = (): void => {
    press(store, now(), flagPanelAction, notice);
    store.setFlagPanel(false);
  };
  const follow = (): void => {
    const { flagPanel, flagPanelByKey, name } = store.getSnapshot();
    const shown = flagPanel && name !== "";
    if (shown && layer === null) {
      layer = input.open({
        onClose: () => {
          layer = null;
          store.setFlagPanel(false);
        },
        keys: { ...(keys.code("flag") === "Tab" ? {} : { [keys.code("flag")]: panelKey }), ...(flagPanelByKey ? { Enter: enter } : {}) },
      });
    } else if (!shown && layer !== null) {
      const open = layer;
      layer = null;
      open.close();
    }
  };
  const unsubscribe = store.subscribe(follow);
  const unlisten = input.onKey((event) => {
    const { flagPanel, name, vitals } = store.getSnapshot();
    if (event.repeat || event.defaultPrevented || !actionPress(keys, "flag", event) || name === "") {
      return;
    }
    if (flagPanel) {
      if (layer !== null) {
        event.preventDefault();
        panelKey();
      }
      return;
    }
    if (vitals?.dead === true || input.depth > under()) {
      return;
    }
    event.preventDefault();
    store.setFlagPanel(true, true);
  });
  follow();
  return () => {
    unsubscribe();
    unlisten();
    layer?.close();
  };
}
